import type { PaletteColor } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'

export type ColorObject = {
  [key: string]: string
}

export interface NeutralColors {
  50: string
  100: string
  200: string
  300: string
  400: string
  500: string
  600: string
  700: string
  800: string
  900: string
}

const withAlphas = (color: PaletteColor): PaletteColor => {
  return {
    ...color,
    alpha4: alpha(color.main, 0.04),
    alpha8: alpha(color.main, 0.08),
    alpha12: alpha(color.main, 0.12),
    alpha20: alpha(color.main, 0.2),
    alpha30: alpha(color.main, 0.3),
    alpha50: alpha(color.main, 0.5),
  }
}

export const neutral: NeutralColors = {
  50: '#F8F9FA',
  100: '#F3F4F6',
  200: '#E5E7EB',
  300: '#D2D6DB',
  400: '#9DA4AE',
  500: '#6C737F',
  600: '#4D5761',
  700: '#2F3746',
  800: '#1C2536',
  900: '#111927',
}

export const nabuColors: ColorObject = {
  ambre: '#E67E22',
  brown: '#663C00',
  blackColor: '#000000',
  blackColor2: '#111111',
  blackColor3: '#111927',
  blackOpacity: '#ff000011',
  blueAzure: '#1976d2',
  blueBarbelLight: '#8193D1',
  blueDark: '#123456',
  blueDarkGrey: '#2F3746',
  blueMajorelle: '#004EEB',
  bluePastel: '#789abc',
  blueViolet: '#11232761',
  brownVelvet: '#F044381A',
  greenCyan: '#2CA398',
  greenCyan2: '#06B7B2',
  greenDark: '#1119271F',
  greenEmeraude: '#18b464',
  greenPine: '#217A72',
  grey: '#D3D3D3',
  greyConcrete: '#F2F4F7',
  greyIron: '#C4C4C4',
  greySteel: '#AAAAAA',
  greyColorLight: '#F4F4F4',
  greyColorLight2: '#EEEEEE',
  greyColorLight3: '#9DA0A6',
  greyBorder: '#555555',
  greyBorder2: '#6C737F',
  greyBorder3: '#333333',
  greyColorDark: '#999999',
  greyColorDarkest: '#666666',
  greyColorDarkest2: '#777777',
  orange: '#F79009',
  red: '#F70D1A',
  red2: '#ed6c02',
  redMadderColor: '#E74C3C',
  redStrawberry: '#D32F2F',
  redBrick: '#f5a14211',
  turquoise: '#C0E0DC',
  violetLight: '#f5a14288',
  whiteColor: '#FFFFFF',
  yellowLight: '#FFF4E5',
}

export const blue = withAlphas({
  lightest: '#F5F8FF',
  light: '#EBEFFF',
  main: '#2970FF',
  dark: '#004EEB',
  darkest: '#00359E',
  contrastText: '#FFFFFF',
})

export const green = withAlphas({
  lightest: '#F6FEF9',
  light: '#EDFCF2',
  main: '#16B364',
  dark: '#087443',
  darkest: '#084C2E',
  contrastText: '#FFFFFF',
})

export const cyan = withAlphas({
  lightest: '#87E0D8',
  light: '#5FD6CB',
  // main: '#2CA398',
  main: '#06B7B2',
  dark: '#217A72',
  darkest: '#16514C',
  contrastText: '#FFFFFF',
})

export const indigo = withAlphas({
  lightest: '#F5F7FF',
  light: '#EBEEFE',
  main: '#2CA398',
  dark: '#268E85',
  darkest: '#312E81',
  contrastText: '#FFFFFF',
})

export const purple = withAlphas({
  lightest: '#F9F5FF',
  light: '#F4EBFF',
  main: '#9E77ED',
  dark: '#6941C6',
  darkest: '#42307D',
  contrastText: '#FFFFFF',
})

export const success = withAlphas({
  lightest: '#F0FDF9',
  light: '#3FC79A',
  main: '#10B981',
  dark: '#0B815A',
  darkest: '#134E48',
  contrastText: '#FFFFFF',
})

export const info = withAlphas({
  lightest: '#ECFDFF',
  light: '#CFF9FE',
  main: '#06AED4',
  dark: '#0E7090',
  darkest: '#164C63',
  contrastText: '#FFFFFF',
})

export const warning = withAlphas({
  lightest: '#FFFAEB',
  light: '#FEDF89',
  main: nabuColors.orange,
  dark: '#DC6803',
  darkest: '#B54708',
  contrastText: nabuColors.whiteColor,
})

export const error = withAlphas({
  lightest: '#FEF3F2',
  light: '#FEE4E2',
  main: '#F04438',
  dark: '#B42318',
  darkest: '#7A271A',
  contrastText: '#FFFFFF',
})

export const standard = withAlphas({
  lightest: '#F5F5F5',
  light: '#E0E0E0',
  main: '#BDBDBD',
  dark: '#9E9E9E',
  darkest: '#616161',
  contrastText: '#000',
})

export type AlertSeverityColor = {
  border: string
  content: string
  background: string
  icon: string
}

export const alertColors = {
  error: {
    border: '#F8C9C9',
    content: '#B82929',
    background: '#FDECEC',
    icon: '#E63B3B',
  },
  success: {
    border: '#B8E6E2',
    content: '#0A857F',
    background: '#E6F7F6',
    icon: '#06B7B2',
  },
  info: {
    border: '#C3D9F0',
    content: '#1F5BA8',
    background: '#E8F1FB',
    icon: '#3576C8',
  },
  warning: {
    border: '#F4D2A8',
    content: '#B05E10',
    background: '#FDEBD9',
    icon: '#E87A1A',
  },
} as const satisfies Record<string, AlertSeverityColor>

export type KeyboardShortcutsColors = {
  bg: string
  fg: string
  rowFg: string
  muted: string
  line: string
  input: string
  hover: string
  headBg: string
  headFg: string
  accentBg: string
  accentFg: string
  keyBg: string
  keyFg: string
  keyLine: string
  hintBg: string
  hintFg: string
  hintRing: string
  veil: string
}

/** Panneau et pastilles des raccourcis clavier (Déclaration), handoff Claude Design. */
export const keyboardShortcutsColors = {
  light: {
    bg: '#FFFFFF',
    fg: neutral[900],
    rowFg: '#1F2937',
    muted: '#6B7280',
    line: '#E5E9EE',
    input: '#F7F9FB',
    hover: '#F3F6F8',
    headBg: '#EEF2F5',
    headFg: '#124C69',
    accentBg: alertColors.success.background,
    accentFg: '#047A77',
    keyBg: '#FFFFFF',
    keyFg: '#374151',
    keyLine: '#D5DBE2',
    hintBg: neutral[900],
    hintFg: '#FFFFFF',
    hintRing: '#FFFFFF',
    veil: 'rgba(11, 18, 32, 0.45)',
  },
  dark: {
    bg: neutral[900],
    fg: '#F3F5F8',
    rowFg: '#D6DCE4',
    muted: '#9AA3AE',
    line: '#253042',
    input: '#0B1220',
    hover: '#1A2434',
    headBg: '#0B1220',
    headFg: '#5FD4CF',
    accentBg: 'rgba(6, 183, 178, 0.16)',
    accentFg: '#5FD4CF',
    keyBg: '#1F2937',
    keyFg: '#E5E9EE',
    keyLine: '#374151',
    hintBg: alertColors.success.icon,
    hintFg: '#0B1220',
    hintRing: '#0B1220',
    veil: 'rgba(11, 18, 32, 0.45)',
  },
} as const satisfies Record<'light' | 'dark', KeyboardShortcutsColors>
